import { AlertCircle, CheckCircle2, ChevronDown, FileImage, LoaderCircle, UploadCloud, Video } from "lucide-react";
import { FormEvent, useState } from "react";
import { api } from "../lib/api";
import type { RequesterRole } from "../types";
import { viewUrl } from "../App";
import { Turnstile } from "./Turnstile";

const allowedTypes = ["image/jpeg", "image/png", "image/heic", "image/heif", "application/pdf"];

export function ChallengeSection() {
  const [formOpen, setFormOpen] = useState(false);
  const [file, setFile] = useState<File>();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [challengeCode, setChallengeCode] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [role, setRole] = useState<RequesterRole>("student");
  const [studentAge, setStudentAge] = useState("");
  const [publicationConsent, setPublicationConsent] = useState(false);

  function chooseFile(next?: File) {
    setError("");
    if (!next) return setFile(undefined);
    if (!allowedTypes.includes(next.type)) {
      setError("Bitte lade ein JPG-, PNG-, HEIC-Bild oder eine PDF-Datei hoch.");
      return;
    }
    if (next.size > 10 * 1024 * 1024) {
      setError("Die Datei darf maximal 10 MB groß sein.");
      return;
    }
    setFile(next);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!file) return setError("Bitte wähle zuerst eine Aufgabe aus.");
    const form = event.currentTarget;
    const data = new FormData(form);
    const needsPublicationAge = role === "student" && publicationConsent;
    const parsedStudentAge = Number(studentAge);
    if (needsPublicationAge && (!Number.isInteger(parsedStudentAge) || parsedStudentAge < 6 || parsedStudentAge > 99)) {
      return setError("Bitte gib dein Alter an.");
    }
    setSubmitting(true);
    setError("");
    try {
      const input = {
        role,
        firstName: String(data.get("firstName") ?? "").trim(),
        grade: String(data.get("grade") ?? ""),
        topic: String(data.get("topic") ?? "").trim(),
        description: String(data.get("description") ?? "").trim(),
        studentAge: needsPublicationAge ? parsedStudentAge : undefined,
        email: email.trim() || undefined,
        phone: String(data.get("phone") ?? "").trim() || undefined,
        notificationOptIn: Boolean(data.get("notificationOptIn")) && Boolean(email.trim()),
        publicationConsent: role !== "student" || !needsPublicationAge || parsedStudentAge >= 18 ? publicationConsent : false,
        turnstileToken,
      };
      const prepared = await api.prepareChallenge(input, file);
      await api.uploadChallengeFile(prepared.path, prepared.token, file);
      await api.finalizeChallenge(prepared.submissionId);
      setChallengeCode(prepared.challengeCode);
      form.reset();
      setFile(undefined);
      setEmail("");
      setRole("student");
      setStudentAge("");
      setPublicationConsent(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Die Aufgabe konnte nicht hochgeladen werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="challenge-teaser">
      <div className="challenge-teaser-copy">
        <div className="eyebrow">Optional: deine konkrete Aufgabe</div>
        <h2>Du hast gerade eine Aufgabe, bei der du hängst?</h2>
        <p>Schick sie mir und ich zeige dir den Lösungsweg – verständlich und Schritt für Schritt.</p>
      </div>
      <button className="button button-secondary challenge-toggle" type="button" onClick={() => setFormOpen((open) => !open)} aria-expanded={formOpen} aria-controls="challenge-form-panel">
        <UploadCloud /> Aufgabe einsenden <ChevronDown className={formOpen ? "is-rotated" : ""} />
      </button>

      {formOpen && <div className="challenge-form-panel" id="challenge-form-panel">
        <div className="challenge-process" aria-label="Ablauf der Challenge">
          <span><i><FileImage /></i><b>1</b> Aufgabe hochladen</span>
          <span><i><Video /></i><b>2</b> Lösungsweg entsteht</span>
          <span><i><CheckCircle2 /></i><b>3</b> Später hier ansehen</span>
        </div>
        <div className="challenge-form-card">
        {challengeCode ? (
          <div className="success-panel">
            <CheckCircle2 />
            <h3>Challenge angenommen!</h3>
            <p>Merke dir deinen Code. Damit findest du dein Video später in der Galerie.</p>
            <strong className="challenge-code">{challengeCode}</strong>
            <button className="button button-secondary" type="button" onClick={() => setChallengeCode("")}>Weitere Challenge senden</button>
          </div>
        ) : (
          <form onSubmit={submit} className="challenge-form">
            <label className={file ? "dropzone has-file" : "dropzone"}>
              <input type="file" accept=".jpg,.jpeg,.png,.heic,.heif,.pdf,image/jpeg,image/png,image/heic,image/heif,application/pdf" onChange={(event) => chooseFile(event.target.files?.[0])} />
              <UploadCloud />
              {file ? <><strong>{file.name}</strong><span>{(file.size / 1024 / 1024).toFixed(1)} MB · Datei ändern</span></> : <><strong>Aufgabe auswählen</strong><span>JPG, PNG, HEIC oder PDF · maximal 10 MB</span></>}
            </label>
            <p className="form-hint">Bitte entferne Namen, Schule und andere persönliche Angaben aus deiner Aufgabe, wenn sie für die Bearbeitung nicht benötigt werden.</p>
            <div className="form-grid two-cols">
              <label>Ich bin …<select name="role" value={role} onChange={(event) => { setRole(event.target.value as RequesterRole); setStudentAge(""); }}><option value="student">Schüler/in</option><option value="parent">Elternteil</option></select></label>
              <label>Vorname<input name="firstName" required minLength={2} maxLength={60} /></label>
              <label>Klasse<select name="grade" required defaultValue=""><option value="" disabled>Bitte wählen</option>{[6, 7, 8, 9, 10, 11, 12, 13].map((grade) => <option key={grade}>Klasse {grade}</option>)}</select></label>
              <label>Thema<input name="topic" required minLength={2} maxLength={100} placeholder="z. B. Parabeln" /></label>
              <label>E-Mail <em>optional</em><input name="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={160} /></label>
              <label>Telefon <em>optional</em><input name="phone" type="tel" maxLength={40} /></label>
            </div>
            <label>Wo hängst du gerade?<textarea name="description" required minLength={10} maxLength={1200} placeholder="Kurze Beschreibung deiner Frage …" /></label>
            <label className={email ? "check-row" : "check-row is-disabled"}><input name="notificationOptIn" type="checkbox" disabled={!email} /> <span>Benachrichtige mich, wenn die Lösung online ist.</span></label>
            <label className="check-row"><input name="publicationConsent" type="checkbox" checked={publicationConsent} onChange={(event) => setPublicationConsent(event.target.checked)} /> <span>Die anonymisierte Aufgabe darf zusammen mit dem Lösungsvideo öffentlich erscheinen.</span></label>
            {role === "student" && publicationConsent && <>
              <label>Wie alt bist du?<input name="studentAge" type="number" inputMode="numeric" min="6" max="99" required value={studentAge} onChange={(event) => setStudentAge(event.target.value)} /></label>
              {studentAge !== "" && Number(studentAge) < 18 && <p className="form-hint">Bei Minderjährigen veröffentliche ich eingesendete Aufgaben nicht über dieses Formular.</p>}
            </>}
            <Turnstile onToken={setTurnstileToken} />
            {error && <div className="alert alert-error" role="alert"><AlertCircle /> {error}</div>}
            <button className="button button-primary full-width" type="submit" disabled={submitting}>
              {submitting ? <><LoaderCircle className="spin" /> Wird sicher hochgeladen …</> : <><UploadCloud /> Challenge abschicken</>}
            </button>
            <p className="form-hint">Der Original-Upload bleibt privat und wird nach spätestens 90 Tagen gelöscht. Informationen zur Verarbeitung deiner Daten findest du in der <a href={viewUrl("datenschutz")}>Datenschutzerklärung</a>.</p>
          </form>
        )}
        </div>
      </div>}
    </div>
  );
}
